import React, { useReducer, useEffect } from 'react';

import { useSessionStorage } from './useSessionStorage.js';
import { getCookie } from '../utilities.js';

const COOKIE_ORG_ID_FIELD = 'CurrentOrganizationId';
const SESSION_STORAGE_LAYOUT_KEY = 'MkLayout';

const responseToJson = response => {
  if (!response.ok) throw Error(response.statusText);
  return response.json();
};

function navigateTo(targetUrl) {
  window.location = targetUrl;
}

const initialState = {
  isRequestInProgress: false,
  isErrorOnLoad: false,
  isDrawerOpen: false,
  isInitialized: false,
  navigationItems: [],
  selectedNavigationIndices: [],
  env: null,
  sessionInfo: {
    currentOrganizationId: '', // TODO make sure empty string works here
    login: '',
    organizationList: [],
    username: ''
  },
  version: null,
  isOrganizationSelectOpen: false,
  appBarMenuAnchor: null,
  hideNav: false, // for the popout encounter cards in aca, ram, audit, etc
  //clientRoutes: (<div></div>) // TODO see if this can be null
  clientRoutes: null
};

const ACTION_TYPES = {
  INITIALIZE_REQUEST: 'INITIALIZE_REQUEST',
  INITIALIZE_SUCCESS: 'INITIALIZE_SUCCESS',
  INITIALIZE_FAILURE: 'INITIALIZE_FAILURE',
  HIDE_NAV: 'HIDE_NAV',
  OPEN_LEFT_DRAWER: 'OPEN_LEFT_DRAWER',
  CLOSE_LEFT_DRAWER: 'CLOSE_LEFT_DRAWER',
  TOGGLE_COLLAPSED_NAVIGATION_ITEM: 'TOGGLE_COLLAPSED_NAVIGATION_ITEM',
  SET_APP_BAR_MENU_ANCHOR: 'SET_APP_BAR_MENU_ANCHOR',
  CLEAR_APP_BAR_MENU_ANCHOR: 'CLEAR_APP_BAR_MENU_ANCHOR',
  OPEN_ORGANIZATION_SELECT: 'OPEN_ORGANIZATION_SELECT',
  CLOSE_ORGANIZATION_SELECT: 'CLOSE_ORGANIZATION_SELECT',
  CHANGE_ORGANIZATION_REQUEST: 'CHANGE_ORGANIZATION_REQUEST',
  CHANGE_ORGANIZATION_SUCCESS: 'CHANGE_ORGANIZATION_SUCCESS',
  CHANGE_ORGANIZATION_FAILURE: 'CHANGE_ORGANIZATION_FAILURE',
  LOGOUT_REQUEST: 'LOGOUT_REQUEST',
  LOGOUT_SUCCESS: 'LOGOUT_SUCCESS',
  LOGOUT_FAILURE: 'LOGOUT_FAILURE'
};

const ACTIONS = {
  initializeRequest: () => ({
    type: ACTION_TYPES.INITIALIZE_REQUEST
  }),
  initializeSuccess: result => ({
    type: ACTION_TYPES.INITIALIZE_SUCCESS,
    payload: {
      ...result
    }
  }),
  initializeFailure: error => ({
    type: ACTION_TYPES.INITIALIZE_FAILURE,
    error
  }),
  hideNav: () => ({
    type: ACTION_TYPES.HIDE_NAV
  }),
  openLeftDrawer: () => ({
    type: ACTION_TYPES.OPEN_LEFT_DRAWER
  }),
  closeLeftDrawer: () => ({
    type: ACTION_TYPES.CLOSE_LEFT_DRAWER
  }),
  toggleCollapsedNavigationItem: clickedIndex => ({
    type: ACTION_TYPES.TOGGLE_COLLAPSED_NAVIGATION_ITEM,
    payload: {
      clickedIndex
    }
  }),
  setAppBarMenuAnchor: currentTarget => ({
    type: ACTION_TYPES.SET_APP_BAR_MENU_ANCHOR,
    payload: {
      currentTarget
    }
  }),
  clearAppBarMenuAnchor: () => ({
    type: ACTION_TYPES.CLEAR_APP_BAR_MENU_ANCHOR
  }),
  openOrganizationSelect: () => ({
    type: ACTION_TYPES.OPEN_ORGANIZATION_SELECT
  }),
  closeOrganizationSelect: () => ({
    type: ACTION_TYPES.CLOSE_ORGANIZATION_SELECT
  }),
  changeOrganizationRequest: () => ({
    type: ACTION_TYPES.CHANGE_ORGANIZATION_REQUEST
  }),
  changeOrganizationSuccess: () => ({
    type: ACTION_TYPES.CHANGE_ORGANIZATION_SUCCESS
  }),
  changeOrganizationFailure: error => ({
    type: ACTION_TYPES.CHANGE_ORGANIZATION_FAILURE,
    error
  }),
  logoutRequest: () => ({
    type: ACTION_TYPES.LOGOUT_REQUEST
  }),
  logoutSuccess: () => ({
    type: ACTION_TYPES.LOGOUT_SUCCESS
  }),
  logoutFailure: error => ({
    type: ACTION_TYPES.LOGOUT_FAILURE,
    error
  })
};

function reducer(state, action){
  switch (action.type) {
    case ACTION_TYPES.INITIALIZE_REQUEST: {
      return {
        ...state,
        isRequestInProgress: true
      };
    }

    case ACTION_TYPES.INITIALIZE_SUCCESS: {
      const { payload } = action;

      return {
        ...state,
        isRequestInProgress: false,
        isInitialized: true,
        ...payload
      };
    }

    case ACTION_TYPES.INITIALIZE_FAILURE: {
      return {
        ...state,
        isRequestInProgress: false,
        isErrorOnLoad: true
      };
    }

    case ACTION_TYPES.HIDE_NAV: {
      return {
        ...state,
        hideNav: true
      };
    }

    case ACTION_TYPES.OPEN_LEFT_DRAWER: {
      return {
        ...state,
        isDrawerOpen: true
      }
    }

    case ACTION_TYPES.CLOSE_LEFT_DRAWER: {
      return {
        ...state,
        isDrawerOpen: false
      };
    }

    case ACTION_TYPES.TOGGLE_COLLAPSED_NAVIGATION_ITEM: {
      const { payload: { clickedIndex } } = action;

      const isAlreadyCollapsed =
        state.selectedNavigationIndices.indexOf(clickedIndex) === -1;

      const nextSelectedIndices = isAlreadyCollapsed === true
        ? state.selectedNavigationIndices.concat(clickedIndex)
        : state.selectedNavigationIndices.filter(x => x  !== clickedIndex);

      return {
        ...state,
        selectedNavigationIndices: nextSelectedIndices
      };
    }

    case ACTION_TYPES.SET_APP_BAR_MENU_ANCHOR: {
      const { payload: { currentTarget } } = action;

      return {
        ...state,
        appBarMenuAnchor: currentTarget
      };
    }

    case ACTION_TYPES.CLEAR_APP_BAR_MENU_ANCHOR: {
      return {
        ...state,
        appBarMenuAnchor: null
      };
    }

    case ACTION_TYPES.OPEN_ORGANIZATION_SELECT: {
      return {
        ...state,
        isOrganizationSelectOpen: true
      };
    }

    case ACTION_TYPES.CLOSE_ORGANIZATION_SELECT: {
      return {
        ...state,
        isOrganizationSelectOpen: false
      };
    }

    case ACTION_TYPES.CHANGE_ORGANIZATION_REQUEST: {
      return {
        ...state,
        isRequestInProgress: true
      };
    }

    case ACTION_TYPES.CHANGE_ORGANIZATION_SUCCESS: {
      return {
        ...state,
        isRequestInProgress: false
      };
    }

    case ACTION_TYPES.CHANGE_ORGANIZATION_FAILURE: {
      return {
        ...state,
        isRequestInProgress: false
      };
    }

    case ACTION_TYPES.LOGOUT_REQUEST: {
      return {
        ...state,
        isRequestInProgress: true
      };
    }

    case ACTION_TYPES.LOGOUT_SUCCESS: {
      return {
        ...state,
        isRequestInProgress: false
      };
    }

    case ACTION_TYPES.LOGOUT_FAILURE: {
      return {
        ...state,
        isRequestInProgress: false
      };
    }

    default: {
      throw Error('default case useLayout reducer');
    }
  }
}

function isNotUndefined(arg) {
  return typeof arg !== 'undefined';
}

// getClientRoutes takes in handleHideNav func as param
function useLayout(getClientRoutes, backendRoutes) {
  const [layoutState, layoutDispatch] = useReducer(reducer, initialState);
  const [savedLayout, setSavedLayout] = useSessionStorage(
    SESSION_STORAGE_LAYOUT_KEY
  );

  // componentDidMount
  useEffect(() => {
    layoutDispatch(ACTIONS.initializeRequest());
    const clientRoutes = getClientRoutes(handleHideNav);

    if (savedLayout !== null && isNotUndefined(savedLayout)) {
      // hydrate layout from sessionStorage into state
      const { sessionInfo: { currentOrganizationId } } = savedLayout;

      const organizationIdFromCookie = getCookie(COOKIE_ORG_ID_FIELD);
      if (
        (isNotUndefined(currentOrganizationId)
          && isNotUndefined(organizationIdFromCookie))
          && (currentOrganizationId.toString()
          === getCookie(COOKIE_ORG_ID_FIELD).toString())
      ) {
        layoutDispatch(ACTIONS.initializeSuccess({
          ...savedLayout,
          clientRoutes
        }));
      }
    } else {
      // hydrate layout from api call into state and set into sessionStorage
      fetch(backendRoutes.Layout, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      .then(responseToJson)
      .then(responseJson => {
        const { result } = responseJson;

        const reshapeResult = (prevState, resultData) => {
          return {
            ...prevState,
            isInitialized: true,
            navigationItems: resultData.navi,
            env: resultData.env,
            sessionInfo: {
              ...prevState.sessionInfo,
              currentOrganizationId: resultData.currentOrganizationId,
              login: resultData.login,
              organizationList: resultData.orgList,
              username: resultData.userName
            },
            version: resultData.version
          };
        };

        const properlyShapedResult = reshapeResult(layoutState, result);

        setSavedLayout(properlyShapedResult);
        layoutDispatch(ACTIONS.initializeSuccess({
          ...properlyShapedResult,
          clientRoutes
        }));
      })
      .catch(e => layoutDispatch(ACTIONS.initializeFailure(e.toString())));
    }
  }, []);

  function handleChangePage(clickedItem) {
    navigateTo(clickedItem.url);
  }

  function handleChangePassword() {
    const login = (window.btoa)
      ? encodeURIComponent(window.btoa(layoutState.sessionInfo.login))
      : encodeURIComponent(layoutState.sessionInfo.login);

    navigateTo(`${backendRoutes.ChangePasswordPage}/${login}`);
  }

  function handleLogout() {
    layoutDispatch(ACTIONS.logoutRequest());

    return fetch(backendRoutes.MicrositeLogin, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(responseToJson)
    .then(() => {
      layoutDispatch(ACTIONS.logoutSuccess());
      setSavedLayout(null);
      navigateTo(backendRoutes.LoginPage);
    })
    .catch(e => layoutDispatch(ACTIONS.logoutFailure(e.toString())));
  }

  function handleUnauthorized() {
    // this function does not appear to ever be called in the old implementation
    // directly ripping it for now:
    // if (this.state.isInitialized) {
    //   window.location = backendRoutes.UnauthorizedPage;
    // } else {
    //   this.setState(prevState => ({ ...prevState, isLoadingError: true }));
    // }
    if (layoutState.isInitialized) {
      navigateTo(backendRoutes.UnauthorizedPage);
    } else {
      layoutDispatch(ACTIONS.initializeFailure('Unauthorized'));
    }
  }

  function handleDrawerOpen() {
    layoutDispatch(ACTIONS.openLeftDrawer());
  }

  function handleDrawerClose() {
    layoutDispatch(ACTIONS.closeLeftDrawer());
  }

  function handleAppBarMenuOpen(event) {
    const { currentTarget } = event;
    layoutDispatch(ACTIONS.setAppBarMenuAnchor(currentTarget));
  }

  function handleAppBarMenuClose() {
    layoutDispatch(ACTIONS.clearAppBarMenuAnchor());
  }

  function handleOrganizationSelectOpen() {
    layoutDispatch(ACTIONS.openOrganizationSelect());
  }

  function handleOrganizationSelectClose() {
    layoutDispatch(ACTIONS.closeOrganizationSelect());
  }

  function handleOrganizationSelectChange(event) {
    const nextOrganizationId = event.target.value;

    layoutDispatch(ACTIONS.changeOrganizationRequest());

    return fetch(backendRoutes.Layout, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
        'Sec-Fetch-Mode': 'cors'
      },
      body: `=${nextOrganizationId}`
    })
    .then(responseToJson)
    .then((/* responseJson */) => {
      setSavedLayout(null);
      layoutDispatch(ACTIONS.changeOrganizationSuccess());
      navigateTo(backendRoutes.InitialPage);
    })
    .catch(e => layoutDispatch(ACTIONS.changeOrganizationFailure(e.toString())));
  }

  function handleToggleCollapsed(clickedIndex) {
    layoutDispatch(ACTIONS.toggleCollapsedNavigationItem(clickedIndex));
  }

  function handleHideNav() {
    if (layoutState.hideNav !== true) {
      layoutDispatch(ACTIONS.hideNav());
    }
  }

  const layoutApi = {
    handleChangePage,
    handleChangePassword,
    handleLogout,
    handleUnauthorized,
    handleDrawerOpen,
    handleDrawerClose,
    handleAppBarMenuOpen,
    handleAppBarMenuClose,
    handleOrganizationSelectOpen,
    handleOrganizationSelectClose,
    handleOrganizationSelectChange,
    handleToggleCollapsed,
    handleHideNav
  };

  return [
    layoutState,
    layoutApi
  ];
}

export { useLayout };
